import type { CatalogProductSelection } from "../../../src/components/ProductSelection/CatalogProductSelection";
import type { C1_FurnitureProps } from "./C1_FurnitureTypes";
import type { C1_EloItem_Data } from "./C1_EloTypes";
import type { C1_BaseObjectData } from "./C1_BaseObjectData";
import type { RoomType } from "./C1_RoomTypes";

export { RoomType, SPECIAL_EXTERIOR_ROOM_TYPES, isSpecialExteriorRoomType } from "./C1_RoomTypes";

export type { C1_BaseObjectData } from "./C1_BaseObjectData";

export type AxisKind = "v" | "h";
export type OpeningKind = "window" | "door" | "garage-door";
export type OpeningMode = "auto" | "manual";
export type WindowShading = "none" | "blind" | "jalousie";
export type C1_SelectedProductManufacturers = Partial<Record<"Actor" | "Button" | "SensorNew", string>>;
export type DoorType = "interior" | "entrance" | "garden" | "passage";
export type GarageDoorType = "roller" | "sectional" | "up-and-over";
export type WallSide = "left" | "right" | "top" | "bottom";
export type FurniturePosition = { x: number; y: number };
export type C1_PointData = { x: number; y: number };

/** Data of one segment; its start is the preceding endpoint or the room start point. */
export interface C1_WallData {
  /** Independent segment, not part of a room's closed polygon. */
  freeWall?: boolean;
  /** Explicit start of a free wall; ordinary walls use the preceding room vertex. */
  start?: C1_PointData;
  end: C1_PointData;
  visible: boolean;
  /** Explicit wall classification. Undefined means that it is derived from neighboring rooms. */
  exterior?: boolean;
  /** Persisted reference to the matching wall segment of the adjacent room. */
  partnerWallId?: string;
}

export const C1_FloorFinish = {
  None: "none",
  General1: "general-1",
  General2: "general-2",
  Parquet1: "parquet-1",
  Parquet2: "parquet-2",
  Tiles1: "tiles-1",
  Tiles2: "tiles-2",
  Paving1: "paving-1",
  Paving2: "paving-2",
  Terrace: "terrace",
  Grass: "grass",
} as const;
export type C1_FloorFinish = (typeof C1_FloorFinish)[keyof typeof C1_FloorFinish];

export { FurnitureCategory } from "./C1_FurnitureCategory";

export type RoomTemplate = {
  key: string;
  type: RoomType;
  defaultWidth?: number;
  defaultHeight?: number;
  defaultFloorFinish?: C1_FloorFinish;
  description: {
    de: string;
    en: string;
    es?: string;
  };
};

export interface C1_ModelSettingsData {
  eloHeights?: import("../model/C1_EloHeights").C1_EloHeightSetting[];
  selectObjectsBeforeMoving?: boolean;
  showTags: boolean;
  tagByIndex: boolean;
  tagEditable: boolean;
  supportsDALI: boolean;
  defaultOpeningShading?: WindowShading;
  defaultOpeningContact?: boolean;
  wallThicknessExterior: number;
  wallThicknessInterior: number;
  wallColorExterior: string;
  wallColorInterior: string;
  wallStrokeWidth: number;
  furnitureStrokeWidth: number;
  furnitureStrokeColor: string;
  furnitureFillColor: string;
  floorColorGeneral1?: string;
  floorColorGeneral2?: string;
  /** Globale Deckkraft aller Raum-Bodenbeläge in Prozent (0 = nicht rendern). */
  floorFinishOpacity?: number;
  /** Kontrast von ELO-Produktgrafiken in Prozent. */
  eloGraphicContrast?: number;
  eloItemScale?: number;
  /** @deprecated Legacy project data; ignored in favor of shared generic product defaults. */
  buttonDefaults?: import("./C1_EloTypes").C1_ButtonDefaultsByType;
}
export interface C1_AxisData {
  /** Initial guide axis for a PDF without room contours; cleanup retains it only when used by rooms. */
  pdfBoundary?: boolean;
  /** Owning ESTADA floor when axes are independent between floors. */
  floorId?: string;
  id: string;
  kind: AxisKind;
  pos: number;
  label?: string;
  locked?: boolean;
}
export interface C1_FurnitureData extends C1_BaseObjectData {
  definitionId: string;
  side?: WallSide;
  wallDocked?: "L" | "R" | "M";
  /** Persistent reference to the ESTADA room wall used for docking. */
  _wallId?: string;
  reflect?: boolean;
  relativePosition: FurniturePosition;
  baseProps: C1_FurnitureProps;
}

export interface C1_RoomData extends C1_BaseObjectData {
  type: RoomType;
  /** Optional room-relative label position; defaults to x=.5/y=.75. */
  roomTextPosition?: C1_PointData;
  floorFinish?: C1_FloorFinish;
  daliAsDefault?: boolean;
  hiddenWalls?: WallSide[];
  /** First polygon point. The ordered walls contain all following endpoints. */
  startPoint: C1_PointData;
  furnitures?: C1_FurnitureData[];
  openings?: C1_OpeningData[];
}
export interface C1_OpeningData extends C1_BaseObjectData {
  kind: OpeningKind;
  mode: OpeningMode;
  doorType?: DoorType;
  garageDoorType?: GarageDoorType;
  slidingDoor?: boolean;
  doorLeafCount?: number;
  /** Legacy location hint; ownership is represented by C1_RoomData.openings. */
  roomId?: string;
  side: WallSide;
  /** Persistent reference to the wall containing this opening. */
  _wallId?: string;
  width: number;
  offset: number;
  reflect?: boolean;
  windowShading?: WindowShading;
  /** Compass direction in degrees (0 = north), calculated for windows from wall side and project north. */
  direction?: number;
}
export type C1_FloorPdfSource = {
  /** ID of the original PDF in Documents / Building plans. */
  documentId: string;
  /** One-based PDF page number. */
  page: number;
  /** Denominator of 1:scale. */
  scale: number;
  /** PDF viewport points, with origin at the page's top left and y pointing down.
   * This point maps to (0, 0) in the imported floor's metre coordinates. */
  originPdf: C1_PointData;
  /** Original PDF viewport size in points, independent of room axes. */
  pageSize?: { width: number; height: number };
};

export interface C1_FloorData extends C1_BaseObjectData {
  name: string;
  level: number;
  ownCabinet: boolean;
  pdfSource?: C1_FloorPdfSource;
}
export interface C1_SerializedFloorData extends C1_FloorData {
  rooms: C1_RoomData[];
}
export type C1_HouseType =
  | "detachedHouse"
  | "semiDetachedHouseLeft"
  | "semiDetachedHouseRight"
  | "terracedHouse"
  | "apartment";
export type C1_EquipmentStandard = 1 | 2 | 3 | 4 | 5;
export type C1_DisplayMode = "design" | "technical" | "din";

export type C1_MasterPdfChapterId = import("../../../src/pdf/PdfExportConfig").PdfChapterId;
export type C1_PdfPlanMode = import("../../../src/pdf/PdfExportConfig").PdfPlanMode;
export type C1_MasterPdfChapterData = import("../../../src/pdf/PdfExportConfig").PdfChapterConfig;
export type C1_MasterPdfConfigData = import("../../../src/pdf/PdfExportConfig").PdfExportConfig;

export interface C1_ProductData extends C1_BaseObjectData, CatalogProductSelection {}

export interface C1_ProjectData {
  tagNumbering?: import("../estada/C1_frozenTags").C1_TagNumbering;
  id: string;
  updatedAt: string;
  northAngle: number;
  houseType: C1_HouseType;
  buildingWidth: number;
  buildingLength: number;
  floorDistance: number;
  equipmentStandard: C1_EquipmentStandard;
  selectedProductManufacturers: C1_SelectedProductManufacturers;
  masterPdfConfig?: C1_MasterPdfConfigData;
  settings: C1_ModelSettingsData;
  axes: C1_AxisData[];
}
export interface C1_ModelData {
  settings: C1_ModelSettingsData;
  axes: C1_AxisData[];
  floors: C1_SerializedFloorData[];
  currentFloorId: string;
}

export type WithOptionalId<T extends { id: string }> = Omit<T, "id" | "tag"> & {
  id?: string;
  tag?: string;
};

export { C1_BaseObject, C1_ChangeEvent } from "../model/C1_BaseObject";
export { C1_ModelSettings } from "../model/C1_ModelSettings";
export { C1_Axis } from "../model/C1_Axis";
export { C1_Furniture } from "../model/C1_Furniture";
export type { C1_Room } from "../model/C1_Room";
export { C1_Opening } from "../model/C1_Opening";
export type { C1_Floor } from "../model/C1_Floor";
export type { C1_State } from "../model/C1_State";
